import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  Renderer2,
} from '@angular/core';

@Directive({
    selector: '[libDetectOverflow]',
    standalone: false
})
export class DetectOverflowDirective implements AfterViewInit, OnDestroy {
  private static readonly overflowTolerancePx = 1;
  private mutationObserver: MutationObserver | null = null;
  // @ts-ignore
  private resizeObserver: ResizeObserver | null = null;
  private lastOverflowState: boolean | null = null;
  private readonly scheduledChecks: Array<ReturnType<typeof setTimeout>> = [];
  private isDestroyed = false;
  private overflowDetectionEnabled = true;

  @Output() overFlowDetected = new EventEmitter<boolean>();
  @Input() set libDetectOverflow(value: boolean | '' | null | undefined) {
    const enabled = value !== false && value !== null;
    if (this.overflowDetectionEnabled === enabled) {
      return;
    }

    this.overflowDetectionEnabled = enabled;
    if (enabled) {
      this.checkForOverflow();
      return;
    }

    this.clearOverflowVisualization();
  }

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
  ) {}

  /**
   * Called once the view is initialized. We set up:
   *   - A MutationObserver to detect content changes within the element.
   *   - An initial overflow check.
   */
  ngAfterViewInit(): void {
    this.isDestroyed = false;
    // Initial check
    this.checkForOverflow();
    this.scheduleOverflowCheck(100);
    this.scheduleOverflowCheck(200);
    this.scheduleOverflowCheck(300);

    // Observe DOM changes inside the element
    this.mutationObserver = new MutationObserver(() => {
      this.checkForOverflow();
    });

    this.mutationObserver.observe(this.el.nativeElement, {
      childList: true, // Watch for added or removed direct children
      subtree: true, // Watch all descendants
      characterData: true, // Watch text changes
    });

    if ('ResizeObserver' in window) {
      // @ts-ignore
      this.resizeObserver = new ResizeObserver(() => {
        this.checkForOverflow();
      });
      this.resizeObserver.observe(this.el.nativeElement);
    } else {
      console.warn('ResizeObserver is not supported by this browser');
      // Optionally: fallback to window:resize event listener
      // (Not shown here; see below if you want a fallback approach)
    }
  }

  /**
   * Called on window resize. This ensures we re-check overflow
   * if the container size changes.
   */
  @HostListener('window:resize')
  onWindowResize() {
    this.checkForOverflow();
  }

  private scheduleOverflowCheck(delayMs: number): void {
    const timeout = setTimeout(() => {
      const index = this.scheduledChecks.indexOf(timeout);
      if (index >= 0) {
        this.scheduledChecks.splice(index, 1);
      }
      this.checkForOverflow();
    }, delayMs);
    this.scheduledChecks.push(timeout);
  }

  /**
   * Core logic to detect overflow (compare scroll size vs. client size).
   * If overflow is detected, add a class; otherwise, remove it.
   */
  private checkForOverflow() {
    if (this.isDestroyed) {
      return;
    }

    const el = this.el.nativeElement;
    if (!this.overflowDetectionEnabled) {
      this.clearOverflowVisualization();
      return;
    }

    const isOverflowing =
      el.scrollWidth >
        el.clientWidth + DetectOverflowDirective.overflowTolerancePx ||
      el.scrollHeight >
        el.clientHeight + DetectOverflowDirective.overflowTolerancePx;

    if (isOverflowing) {
      this.renderer.addClass(el, 'overflowDetected');
    } else {
      this.renderer.removeClass(el, 'overflowDetected');
    }

    if (this.lastOverflowState === isOverflowing) {
      return;
    }

    this.lastOverflowState = isOverflowing;
    this.overFlowDetected.emit(isOverflowing);
  }

  private clearOverflowVisualization(): void {
    this.renderer.removeClass(this.el.nativeElement, 'overflowDetected');

    if (this.lastOverflowState !== true) {
      return;
    }

    this.lastOverflowState = false;
    this.overFlowDetected.emit(false);
  }

  /**
   * Clean up the observer to avoid memory leaks.
   */
  ngOnDestroy(): void {
    this.isDestroyed = true;
    for (const timeout of this.scheduledChecks.splice(0)) {
      clearTimeout(timeout);
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.el.nativeElement);
      this.resizeObserver = null;
    }
  }
}
